import React, { useEffect, useState, useReducer } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { useLocation } from 'react-router';
import './App.css';

import styled from "styled-components";

import { isMobile, isTablet } from './Utils/deviceDetect';
import LocalStorage from './Model/Share/Network/LocalStorage';
import FetchModule from './Model/Share/Network/FetchModule';
import { LanguageContextProvider } from './Context/LanguageContext';
import { ThemeContextProvider } from './Context/ThemeContext';
import ChannelTalk from "./Model/Share/Network/ChannelTalk";
import { hasNavCode, isSubAccount } from './Model/Share/Normal/navPermission.js';

import DefaultView from './Model/Share/Normal/DefaultView.js';
import NavigationBar from "./Model/Share/Normal/NavigationBar.js";
import NoteNavigationBar from "./Model/Share/Normal/NoteNavigationBar.js";
import BottomInfo from './Model/Share/Normal/BottomInfo.js';

import HomeMain from './View/Home.js';
// Ai Page (씬 클라이언트 — 접수·폴링. /ai 메뉴 연결, /ai-test 는 검증 스크립트용 같은 화면) //
import AiPageServer from './AiManagerRefact/Page/AiPage.js';
import AccountPage from './View/AccountPage/AccountPage.js';
import CertificationPage from './View/AccountPage/CertificationPage.js';
import EmailCertificationPage from './View/AccountPage/EmailCertificationPage.js';
import SearchIdentifierPage from './View/AccountPage/SearchIdentifierPage.js';
import PermutePage from './View/AccountPage/PermutePage.js';
import PasswordPermutePage from './View/AccountPage/PasswordPermutePage.js';
import FindIdPage from "./View/AccountPage/FindIdPage";

import NewMemberRegistrationPage from "./MemberRegistration/Page/MemberRegistrationPage.js";
import ValidMemberInfoPage from "./View/MemberPage/ValidMemberInfoPage.js";
import ExpiredMemberInfo from './View/MemberPage/ExpiredMemberInfoPage.js';
import WaittingMemberPage from "./View/MemberPage/WaittingMemberPage";
import MemberDetailPage from "./View/MemberPage/MemberDetailPage";
import PartnerGymMemberPage from "./View/MemberPage/PartnerGymMemberPage";
import PartnerGymMemberDetailPage from "./View/MemberPage/PartnerGymMemberDetailPage";

import ClassManagePage from "./View/ClassPage/ClassManagePage";
import SchedulePage from './View/SchedulePage/SchedulePage.js';

import OneOnOnePage from './Exercise/Page/OneOnOnePage';
import GroupPage from "./Exercise/Page/GroupPage.js";
import RankingPage from "./View/NotePage/RankingPage";
import SharePage from "./View/NotePage/SharePage";
import TimeLinePage from "./View/NotePage/TimeLinePage";

import BoardPage from "./Board/Page/BoardPage.js";
import FullScreenBoardPage from "./Board/Page/FullScreenBoardPage.js";

import SettingPage from './View/SettingPage.js';
import MyStoreSettingPage from "./MyStoreSetting/View/MyStoreSettingPage.js";

import StatisticsPage from './View/StatisticsPage.js';

import LockerPage from './View/LockerPage.js';

import NoticeListPage from './View/NoticePage/NoticeListPage.js';
import NoticeCreatePage from "./View/NoticePage/NoticeCreatePage";
import NoticePage from './View/NoticePage/NoticePage';
import NoticeModifyPage from "./View/NoticePage/NoticeModifyPage";

import MessageSendPage from "./View/Message/Page/MessageSendPage.js";

import ZonePaymentSubscribeCompleteToss from "./View/PaymentPage/ZonePaymentSubscribeCompleteToss";

import ServicePage from "./View/RulesPage/ServicePage";
import LocationPage from "./View/RulesPage/LocationPage";
import PrivacyPage from "./View/RulesPage/PrivacyPage";
import PromotionPage from "./View/RulesPage/PromotionPage";
import RefundPage from "./View/RulesPage/RefundPage";
import RulerPage from "./View/RulerPage";

import AppDeepLink from "./View/AppDeepLinkPage.js";
import TempKeypadPage from "./View/KeypadPage/TempKeypadPage";
import KeypadForAttendancePage from "./View/KeypadPage/KeypadForAttendancePage";

// 페이지 경로 추적 컴포넌트 //
function PageTracker(props) {
  const { setNowPage } = props;
  const location = useLocation();
  const defaultURL = 'https://link-zone.io';

  useEffect(() => {
    setNowPage(defaultURL + location.pathname);
  }, [location]);

  return null;
}

// 경로 → 필요 nav 권한 코드 (null 이면 공통 경로, 권한 무관 허용) //
function getRequiredNavCode(pathname) {
  if (pathname.startsWith('/setting')) return 'SETTING';
  if (pathname.startsWith('/ai')) return 'AI';
  if (pathname.startsWith('/member')) return 'MEMBER';
  if (pathname.startsWith('/class')) return 'CLASS';
  if (pathname.startsWith('/statistics')) return 'STATS';
  if (pathname.startsWith('/locker')) return 'LOCKER';
  if (pathname.startsWith('/board')) return 'BOARD';
  if (pathname.startsWith('/note')) return 'NOTE';
  if (pathname.startsWith('/notice') || pathname.startsWith('/message')) return 'NOTICE';

  return null;
}

// 본계정(MAIN) 전용 경로 — 부계정이 진입하면 마운트 즉시 본계정 전용 API(terms · billing · zone_subscribe)를 호출해 403 이 연달아 난다 //
const MAIN_ONLY_PATHS = ['/mypage', '/payment'];

// 부계정(SUB) 권한 밖 경로 직접 접근 차단 — navItems 미포함 경로(특히 /setting)는 /main 으로 //
// useEffect 로 되돌리면 되돌리기 전에 해당 페이지가 이미 마운트돼 첫 API 호출이 나가므로,
// 라우트를 아예 렌더하지 않는 방식으로 막는다 //
function SubAccountRouteGate(props) {
  const { children } = props;
  const location = useLocation();

  if (!isSubAccount()) return children;

  if (MAIN_ONLY_PATHS.some(path => location.pathname.startsWith(path))) return <Redirect to="/main" />;

  const requiredNavCode = getRequiredNavCode(location.pathname);

  if (requiredNavCode === null) return children;

  if (!hasNavCode(requiredNavCode)) return <Redirect to="/main" />;

  return children;
}

export default function App() {

  const [nowPage, setNowPage] = useState('/');
  const [isSessionReady, setIsSessionReady] = useState(false);

  const [isViewAdminChangeLogin, setIsViewAdminChangeLogin] = useState(false);

  // 서버 권한(nav) 재조회 반영용 강제 리렌더 — 권한 변경 시에만 NavigationBar 재평가 유도 //
  const [, forceNavRerender] = useReducer((count) => count + 1, 0);

  // 세션 초기화 (탭별 로그인 관리) + 로그인 상태면 서버 권한 재조회 //
  useEffect(() => {
    LocalStorage.initSession(() => {
      setIsSessionReady(true);

      if (window.sessionStorage.getItem('Token')) refreshNavPermission();
    });
  }, []);

  // 모바일/태블릿 접속 감지 //
  useEffect(() => {
    if (isTablet || isMobile) {
      let url = window.location.href;
    }
  }, [])

  // Channel Talk 초기화 //
  useEffect(() => {
    ChannelTalk.boot({ "pluginKey": "d2f4dfce-95e8-498e-aed3-84804b9d3772" });
  }, [])

  // 서버 권한(nav) 재조회 — 새로고침/복원 시 최신 권한을 sessionStorage 에 반영 (실패 시 기존 백업값 유지) //
  function refreshNavPermission() {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getIndividualData('account/nav', 'GET');

          if (responseData.status !== 200 || !Array.isArray(responseData.navItems)) return;

          const nextNavItems = JSON.stringify(responseData.navItems);
          const prevNavItems = window.sessionStorage.getItem('NavItems');
          const prevAccountType = window.sessionStorage.getItem('AccountType');
          const prevCanSwitchAccount = window.sessionStorage.getItem('CanSwitchAccount');

          window.sessionStorage.setItem('NavItems', nextNavItems);

          if (responseData.accountType) window.sessionStorage.setItem('AccountType', responseData.accountType);
          if (responseData.canSwitchAccount !== undefined) window.sessionStorage.setItem('CanSwitchAccount', responseData.canSwitchAccount);

          LocalStorage.saveSession();

          // 권한이 실제로 바뀐 경우에만 리렌더 — 미변경(대다수)엔 렌더 비용 0 //
          const isChanged = prevNavItems !== nextNavItems
            || prevAccountType !== responseData.accountType
            || prevCanSwitchAccount !== String(responseData.canSwitchAccount);

          if (isChanged) forceNavRerender();
        }
        catch (e) {
          // 네트워크 오류 등 — 기존 백업값 유지 //
        }
      }
    )();
  }

  // AI 페이지 경로 여부 (/ai · /ai-test 동일 레이아웃 적용) //
  function isAiPage() {
    return nowPage.endsWith('/ai') || nowPage.endsWith('/ai-test');
  }

  // 페이지 최소크기 결정 //
  function decideContainerMinWidth() {
    if (isAiPage()) return `910px`;
    if (nowPage.includes('/note/') || nowPage.includes('/full/board/') || nowPage.includes('/keypad') || nowPage.includes('/identifier') || nowPage.includes('/rule/')) return 'auto';
    else if (nowPage.includes('/full/board/')) return '900px';
    else return '1195px';
  }

  // 페이지 최대 크기 결정 //
  function decideContentWrapperMaxWidth() {
    if (isAiPage()) return 'none';
    if (nowPage.includes('/note/') || nowPage.includes('/full/board/') || nowPage.includes('/keypad') || nowPage.includes('/identifier') || nowPage.includes('/rule/')) return 'auto';
    else return '1195px';
  }

  // ContentWrapper justify-content flex-start 여부 결정 //
  function decideIsFlexStart() {
    const flexStartPaths = ['/main', '/member', '/class', '/statistics', '/locker', '/board', '/notice', '/setting'];
    return flexStartPaths.some(path => nowPage.includes(path));
  }

  if (!isSessionReady) return null;

  return (
    <ThemeContextProvider>
    <LanguageContextProvider>
    <Container
      minWidth={decideContainerMinWidth()}
      isNotePage={nowPage.includes('/note/')}>
      <Router>
        <PageTracker setNowPage={setNowPage} />
        <DefaultView />
        {
          nowPage.includes('/note/') && !nowPage.includes('/note/program') && !nowPage.includes('/note/one-on-one') &&
          <NoteNavigationBar />
        }
        <ContentWrapper
          maxWidth={decideContentWrapperMaxWidth()}
          isNotePage={nowPage.includes('/note/')}
          isFlexStart={decideIsFlexStart()}>
          {
            !nowPage.includes('/note/') && !isAiPage() && !nowPage.includes('/keypad') && !nowPage.includes('/identifier') &&
            <NavigationBar
              nowPage={nowPage}
              setNowPage={setNowPage} />
          }
          <SubAccountRouteGate>
            <Switch>
              <Route path="/login" exact component={AccountPage} />
              <Route path="/main" exact component={HomeMain} />
              <Route path="/ai" exact component={AiPageServer} />
              <Route path="/ai-test" exact component={AiPageServer} />
              <Route path="/certification/:certificationString" exact component={EmailCertificationPage} />
              <Route path="/certification" exact component={CertificationPage} />
              <Route path="/identifier" exact component={SearchIdentifierPage} />
              <Route path="/permute/:token/:name" exact component={PasswordPermutePage} />
              <Route path="/permute/" exact component={PermutePage} />
              <Route path="/mypage" exact component={MyStoreSettingPage} />
              <Route path="/find-id" exact component={FindIdPage} />
              <Route path="/member/valid" exact component={ValidMemberInfoPage} />
              <Route path="/member/valid/detail/:memberId" exact component={MemberDetailPage} />
              <Route path="/member/valid/detail/:memberId/:division" exact component={MemberDetailPage} />
              <Route path="/member/expired/detail/:memberId" exact component={MemberDetailPage} />
              <Route path="/member/expired" exact component={ExpiredMemberInfo} />
              <Route path="/member/registration" exact component={NewMemberRegistrationPage} />
              <Route path="/member/waitting" exact component={WaittingMemberPage} />
              <Route path="/member/partner_gym" exact component={PartnerGymMemberPage} />
              <Route path="/member/partner_gym/detail/:memberId" exact component={PartnerGymMemberDetailPage} />
              <Route path="/class" exact component={ClassManagePage} />
              <Route path="/schedule" exact component={SchedulePage} />
              <Route path="/note/ranking" exact component={RankingPage} />
              <Route path="/note/share" exact component={SharePage} />
              <Route path="/note/timeline" exact component={TimeLinePage} />
              <Route path="/note/program" exact component={GroupPage} />
              <Route path="/note/one-on-one" exact component={OneOnOnePage} />
              <Route path="/board" exact component={BoardPage} />
              <Route path="/full/board/:token" exact component={FullScreenBoardPage} />
              <Route path="/setting" exact component={SettingPage} />
              <Route path="/statistics" exact component={StatisticsPage} />
              <Route path="/locker" exact component={LockerPage} />
              <Route path="/notice" exact component={NoticeListPage} />
              <Route path="/notice/details/:noticeId" exact component={NoticePage} />
              <Route path="/notice/create" exact component={NoticeCreatePage} />
              <Route path="/notice/modify/:noticeId" exact component={NoticeModifyPage} />
              <Route path="/message" exact component={MessageSendPage} />
              <Route path="/rule/service" exact component={ServicePage} />
              <Route path="/rule/location" exact component={LocationPage} />
              <Route path="/rule/privacy" exact component={PrivacyPage} />
              <Route path="/rule/promotion" exact component={PromotionPage} />
              <Route path="/rule/refund" exact component={RefundPage} />
              <Route path="/payment/zone/subscribe/:status" exact component={ZonePaymentSubscribeCompleteToss} />
              <Route path="/app/:scheme" exact component={AppDeepLink} />
              <Route path="/ready/keypad/:storeId/:accessToken/:refreshToken" exact component={TempKeypadPage} />
              <Route path="/keypad" exact component={KeypadForAttendancePage} />
              <Route path="/ruler" exact component={RulerPage} />
              <Redirect to="/main" />
            </Switch>
          </SubAccountRouteGate>
        </ContentWrapper>
        <BottomInfo
          isViewAdminChangeLogin={isViewAdminChangeLogin}
          setIsViewAdminChangeLogin={setIsViewAdminChangeLogin} />
      </Router>
    </Container>
    </LanguageContextProvider>
    </ThemeContextProvider>
  );
}

const Container = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  top: 0;
  left: 0;

  width: 100vw;
  min-width: ${props => props.minWidth};

  background-color: ${props => props.theme.background};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: ${props => props.isNotePage || props.isFlexStart ? `flex-start` : `center`};
  align-items: flex-start;

  width: 100%;
  max-width: ${props => props.maxWidth};

  height: 100%;
`;
